import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Box,
    Button,
    Card,
    CardContent,
    TextField,
    Typography,
    InputAdornment,
    IconButton,
    Alert,
} from '@mui/material';
import { Eye, EyeOff, ArrowRight, ArrowLeft, UserPlus } from 'lucide-react';
import { sendEmailVerificationOtp, verifyEmailOtp, register } from '../../services/authService';
import { TOKEN_KEY, USER_ROLE_KEY } from '../../utils/constants';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormData {
    name: string;
    lastName: string;
    email: string;
    password: string;
    confirmPassword: string;
}

interface FormErrors {
    name?: string;
    lastName?: string;
    email?: string;
    password?: string;
    confirmPassword?: string;
}

const ParentRegister: React.FC = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [otp, setOtp] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});
    const [otpError, setOtpError] = useState('');

    const [formData, setFormData] = useState<FormData>({
        name: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
    });

    const validateStep1 = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!EMAIL_REGEX.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Please confirm your password';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSendOtp = async () => {
        if (!validateStep1()) {
            return;
        }

        setIsLoading(true);
        try {
            await sendEmailVerificationOtp(formData.email);
            toast.success('OTP sent to your email');
            setActiveStep(1);
        } catch (error: any) {
            toast.error(error.response?.data?.msg || 'Failed to send OTP');
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyAndRegister = async () => {
        if (!otp.trim()) {
            setOtpError('Please enter the OTP');
            return;
        }

        setIsLoading(true);
        setOtpError('');

        try {
            await verifyEmailOtp(formData.email, otp);
        } catch (error: any) {
            setOtpError(error.response?.data?.msg || 'Invalid OTP');
            setIsLoading(false);
            return;
        }

        try {
            const response = await register({
                name: formData.name.trim(),
                lastName: formData.lastName,
                email: formData.email.trim(),
                password: formData.password,
                role: 'parent',
            });

            if (response.token) {
                localStorage.setItem(TOKEN_KEY, response.token);
                localStorage.setItem(USER_ROLE_KEY, 'parent');
                toast.success('Registration successful!');
                navigate('/parent-dashboard');
            }
        } catch (error: any) {
            const message = error.response?.data?.message || error.response?.data?.msg || 'Registration failed';
            setOtpError(message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [field]: e.target.value });
        if (errors[field]) {
            setErrors({ ...errors, [field]: undefined });
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                py: 6,
                background:
                    'radial-gradient(circle at top, rgba(90, 75, 252, 0.12), transparent 40%), radial-gradient(circle at bottom, rgba(0, 184, 148, 0.16), transparent 45%), #f4f6fb',
            }}
        >
            <Card
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    {/* Header */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                bgcolor: 'rgba(108, 92, 231, 0.1)',
                                mb: 2,
                            }}
                        >
                            <UserPlus size={32} color="#6c5ce7" />
                        </Box>
                        <Typography variant="h5" fontWeight="bold" gutterBottom>
                            Parent Registration
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Create an account to track your child's progress
                        </Typography>
                    </Box>

                    {/* Step 1: Account Details */}
                    {activeStep === 0 && (
                        <Box>
                            <TextField
                                fullWidth
                                label="Full Name"
                                value={formData.name}
                                onChange={handleChange('name')}
                                error={!!errors.name}
                                helperText={errors.name}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Email Address"
                                type="email"
                                value={formData.email}
                                onChange={handleChange('email')}
                                error={!!errors.email}
                                helperText={errors.email}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Password"
                                type={showPassword ? 'text' : 'password'}
                                value={formData.password}
                                onChange={handleChange('password')}
                                error={!!errors.password}
                                helperText={errors.password}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />

                            <TextField
                                fullWidth
                                label="Confirm Password"
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={formData.confirmPassword}
                                onChange={handleChange('confirmPassword')}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 3 }}
                            />

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSendOtp}
                                disabled={isLoading}
                                endIcon={<ArrowRight size={20} />}
                                sx={{
                                    py: 1.5,
                                    bgcolor: '#6c5ce7',
                                    '&:hover': { bgcolor: '#5b4bc4' },
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 600,
                                }}
                            >
                                {isLoading ? 'Sending OTP...' : 'Continue'}
                            </Button>
                        </Box>
                    )}

                    {/* Step 2: OTP Verification */}
                    {activeStep === 1 && (
                        <Box>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                We've sent a verification code to <strong>{formData.email}</strong>
                            </Alert>

                            <TextField
                                fullWidth
                                label="Enter OTP"
                                value={otp}
                                onChange={(e) => {
                                    setOtp(e.target.value);
                                    setOtpError('');
                                }}
                                error={!!otpError}
                                helperText={otpError}
                                placeholder="Enter 6-digit code"
                                sx={{ mb: 3 }}
                            />

                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setActiveStep(0)}
                                    startIcon={<ArrowLeft size={20} />}
                                    sx={{ flex: 1, textTransform: 'none' }}
                                >
                                    Back
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleVerifyAndRegister}
                                    disabled={isLoading}
                                    sx={{
                                        flex: 1,
                                        bgcolor: '#6c5ce7',
                                        '&:hover': { bgcolor: '#5b4bc4' },
                                        textTransform: 'none',
                                        fontWeight: 600,
                                    }}
                                >
                                    {isLoading ? 'Verifying...' : 'Complete Registration'}
                                </Button>
                            </Box>
                        </Box>
                    )}

                    {/* Footer */}
                    <Box sx={{ mt: 3, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Already have an account?{' '}
                            <Typography
                                component="span"
                                sx={{ color: '#6c5ce7', cursor: 'pointer', fontWeight: 600 }}
                                onClick={() => navigate('/login')}
                            >
                                Login here
                            </Typography>
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default ParentRegister;
